import { useRouter } from 'next/router'

import GET_PRODUCTS_COUNT from '@/graphql/queries/products/getProductsCount'
import GET_PRODUCTS_HIGHEST_PRICE from '@/graphql/queries/products/getProductsHighestPrice'
import getProductsQuery, {
  ProductQuerySortDirection,
  ProductQuerySortKey,
} from '@/graphql/queries/products/getProductsQuery'
import Filters from '@/services/Filters/Filters'
import ProductType from '@/ts/enums/product/ProductType'
import Aggregate from '@/ts/interfaces/global/Aggregate'
import ProductPrice from '@/ts/interfaces/product/ProductPrice'
import Product from '@/ts/Product'

import useHasura from './useHasura'
import usePagination from './usePagination'
import usePriceRange from './usePriceRange'

function useProducts(type: ProductType) {
  const router = useRouter()

  const currentFilters = Filters.get() || {}
  const priceRange = usePriceRange(100)
  const creator_nickname = router.query.creator

  const variables = { type, ...currentFilters, ...priceRange, creator_nickname }

  const queryAggregate = useHasura<QueryAggregate>(
    'products_aggregate',
    GET_PRODUCTS_COUNT,
    { variables }
  )

  const count = queryAggregate.data?.products_aggregate?.aggregate?.count || 0

  const { limit, offset } = usePagination(count)
  const config = { enabled: count > 0 }

  const queryProducts = useHasura<QueryProducts>(
    ['products', type, router.query.sort_by, router.query.direction],
    getProductsQuery({
      key: router.query.sort_by as ProductQuerySortKey,
      direction: router.query.direction as ProductQuerySortDirection,
    }),
    {
      config,
      variables: { ...variables, limit, offset },
    }
  )

  const queryPrice = useHasura<QueryPrice>(
    'products_highest_price',
    GET_PRODUCTS_HIGHEST_PRICE,
    { config, variables: { type } }
  )

  const isLoading = queryProducts.isFetching && queryProducts.isPreviousData

  const products = queryProducts.data?.products || []
  const maxPrice = Number(queryPrice.data?.prices?.[0]?.value) || 0

  return { products, count, maxPrice, isLoading }
}

interface QueryProducts {
  products: Product[]
}

interface QueryPrice {
  prices: ProductPrice[]
}

interface QueryAggregate {
  products_aggregate: Aggregate
}

export default useProducts
